import { useCallback, useRef, useState } from "react";
import httpClient, { AuthNotReadyError } from "../utils/httpClient";
import { CommunitiesProps } from "../providers/types";
import { sleep } from "../utils/sleep";
import { normalizeCommunities } from "../utils/normalizeCommunities";
import { readCommunitiesCache, writeCommunitiesCache } from "../utils/communityCache";
import { communityCacheKey } from "../utils/communityCacheKey";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 2000;

export const useLoadCommunities = (
    setCommunities: (data: CommunitiesProps[]) => void,
    setCommunities2: (data: CommunitiesProps[]) => void
) => {
    const inFlightRef = useRef<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState(false);

    const resetInFlight = useCallback(() => {
        inFlightRef.current = {};
    }, []);

    const loadCommunities = useCallback(
        async (companyId: number, regionId: number) => {
            const flightKey = `${companyId}.${regionId}`;
            if (inFlightRef.current[flightKey]) return;
            inFlightRef.current[flightKey] = true;
            setIsLoading(true);

            const cacheKey = communityCacheKey(companyId, regionId);

            const apply = (items: CommunitiesProps[]) => {
                const { sorted, deduped } = normalizeCommunities(items);
                setCommunities(sorted);
                setCommunities2(deduped);
                return sorted;
            };

            // Single fetch function with AuthNotReadyError handling
            const fetchOnce = async (): Promise<CommunitiesProps[] | null> => {
                try {
                    const res = await httpClient.get<CommunitiesProps[]>(
                        `/api/cba/communities-summary/${companyId}/${regionId}`
                    );
                    return res.data ?? [];
                } catch (e) {
                    if (e instanceof AuthNotReadyError || (e as any)?.response?.status === 401) {
                        return null; // Silent retry
                    }
                    throw e; // Real error
                }
            };

            try {
                // 1) Hydrate UI from cache immediately
                const cached = readCommunitiesCache(cacheKey);
                if (cached) apply(cached);

                // 2) Fetch fresh data
                let data = await fetchOnce();

                // 3) Retry if auth not ready (null) or empty with no cache
                let attempts = 0;
                while (
                    (data === null || (data.length === 0 && !cached)) &&
                    attempts < MAX_RETRIES
                ) {
                    await sleep(RETRY_DELAY_MS);
                    data = await fetchOnce();
                    attempts++;
                }

                // 4) Don't overwrite valid cached UI with empty/null response
                if (!data || data.length === 0) return;

                // 5) Apply fresh data and update cache
                const sorted = apply(data);
                writeCommunitiesCache(cacheKey, sorted);
            } catch (e) {
                console.error("Error loading communities:", e);
            } finally {
                inFlightRef.current[flightKey] = false;
                setIsLoading(false);
            }
        },
        [setCommunities, setCommunities2]
    );

    return { loadCommunities, isLoading, resetInFlight };
};